'use client';

import React, { useEffect, useState, useRef } from 'react';
import { GameInfoModalWrapper } from '../game-info-modal/subcomponents/game-info-modal-wrapper';
import { getText } from '@/utils/text/text';
import { GameUrl } from '../game/subcomponents/game-url/game-url';
import { GameBadgeImage } from '@/app/components/game-badge-image/game-badge-image';
import { SpinningNumber } from './subcomponents/spinning-number';
import { SvgLoader } from '@/app/elements/svg/loader';
import { Spinner } from '@/app/elements/spinner';
import Lottie from 'react-lottie';

import './styles/default.scss';
import { BRAND } from '@/utils/constants';
import { toGameViewModel } from '@/logic/games/to-game-view-model';
import { POPUPS } from '../popup/constants';
import { useStore } from '@/store/store';
import type { Game as GameType } from '@/types/games';
import { getCategoryPageViewedProps } from '@/tracking/amplitude/events/general/category-page-viewed';

export const formatValue = (value: number) =>
    value.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
        useGrouping: true
    });

const renderWinMessage = (stakeAmount: number, winAmount: number, multiplier: number) => {
    const template = getText({ text: 'components.biggestWin.winMessage' });
    const parts = template.split(/%s/g);
    const elements = [
        <span key="bet-value" className="highlight-value-number has-dollar">
            {formatValue(stakeAmount)}
        </span>,
        <span key="bet" className="highlight-value">
            {getText({ text: 'components.biggestWin.bet' })}
        </span>,
        <span key="amount" className="highlight-value-number has-dollar">
            {formatValue(winAmount)}
        </span>,
        <span key="multiplier-value" className="highlight-value-number">
            {multiplier}x
        </span>,
        <span key="multiplier-label" className="highlight-value">
            {getText({ text: 'components.biggestWin.multiplier' })}
        </span>
    ];

    const result: React.ReactNode[] = [];
    parts.forEach((part: string, index: number) => {
        result.push(part);
        if (index < elements.length) {
            result.push(elements[index]);
        }
    });
    return result;
};
const LOTTIE_SHOW_DELAY = 1500; // Show 1.5s after modal opens (after numbers finish spinning)
const LOTTIE_HIDE_DELAY = 4300; // Hide after 4.3s total (2.8s visible)

type BiggestWinParams = {
    tileData: unknown;
    stake_amount?: number;
    win_amount?: number;
    multiplier?: number;
    rgLink?: string;
    rgLogo?: string;
    isLoading?: boolean;
    hasError?: boolean;
    confettiAnimationUrl?: string;
    isHighlighted?: boolean;
};

export const BiggestWinModal: React.FC = () => {
    const { activePopup, closePopup } = useStore((s) => s.popup);
    const { region, isMobile } = useStore((s) => s?.globals);
    const [backgroundImgUrl, setBackgroundImgUrl] = useState<string>('');
    const { sendEvent } = useStore((state) => state.tracking);

    useEffect(() => {
        const loadBackground = async () => {
            const brand = process.env.BRAND;
            if (brand === BRAND.FANDUEL) {
                const FanduelBackground = await import('@/assets/images/fanduel-biggest-win-background.avif');
                setBackgroundImgUrl(FanduelBackground.default.src);
            } else {
                const MohegansunBackground = await import('@/assets/images/mohegansun-biggest-win-background.avif');
                setBackgroundImgUrl(MohegansunBackground.default.src);
            }
        };
        loadBackground();
    }, []);

    const [lottieData, setLottieData] = useState<Record<string, unknown> | null>(null);
    const [showLottie, setShowLottie] = useState(false);
    const timersRef = useRef<{
        showTimer: ReturnType<typeof setTimeout> | null;
        hideTimer: ReturnType<typeof setTimeout> | null;
    }>({
        showTimer: null,
        hideTimer: null
    });

    const params = activePopup?.params as BiggestWinParams | undefined;
    const isLoading = params?.isLoading;
    const tileData = params?.tileData;
    const stake_amount = params?.stake_amount;
    const win_amount = params?.win_amount;
    const isHighlighted = params?.isHighlighted;

    useEffect(() => {
        const url = params?.confettiAnimationUrl;
        if (!url) {
            setLottieData(null); // Clear previous data if URL is removed
            return;
        }

        fetch(url)
            .then((response) => response.json())
            .then((data) => setLottieData(data))
            .catch((error) => console.error('Error loading Lottie animation:', error));
    }, [activePopup, params?.confettiAnimationUrl]);

    useEffect(() => {
        if (timersRef.current.showTimer) {
            clearTimeout(timersRef.current.showTimer);
            timersRef.current.showTimer = null;
        }
        if (timersRef.current.hideTimer) {
            clearTimeout(timersRef.current.hideTimer);
            timersRef.current.hideTimer = null;
        }

        if (!activePopup || activePopup.type !== POPUPS.BIGGEST_WIN) {
            setShowLottie(false);
            return;
        }

        const isLoadingState = isLoading === true;
        const isLoaded = !isLoadingState;
        const hasAllRequiredData =
            tileData !== undefined && tileData !== null && stake_amount !== undefined && win_amount !== undefined;
        const shouldShowError = isLoaded && !hasAllRequiredData;

        if (!isLoadingState && !shouldShowError && lottieData) {
            timersRef.current.showTimer = setTimeout(() => setShowLottie(true), LOTTIE_SHOW_DELAY);
            timersRef.current.hideTimer = setTimeout(() => setShowLottie(false), LOTTIE_HIDE_DELAY);
        } else {
            setShowLottie(false);
        }

        return () => {
            if (timersRef.current.showTimer) {
                clearTimeout(timersRef.current.showTimer);
            }
            if (timersRef.current.hideTimer) {
                clearTimeout(timersRef.current.hideTimer);
            }
        };
    }, [activePopup, isLoading, tileData, stake_amount, win_amount, lottieData]);

    useEffect(() => {
        if (!activePopup || activePopup.type !== POPUPS.BIGGEST_WIN) {
            return;
        }

        const eventProps = getCategoryPageViewedProps({
            category: 'biggest_win',
            pageName: 'category',
            navigationSource: 'top_nav',
            tag: isHighlighted ? 'notification' : 'null'
        });
        sendEvent([{ destination: 'amplitude', eventProps }]);
    }, [sendEvent, activePopup?.id, isHighlighted]);

    if (!activePopup) {
        return null;
    }

    const { type } = activePopup;

    const { multiplier, rgLink, rgLogo } = (params ?? {}) as BiggestWinParams;

    const closeIcon = <SvgLoader iconName="close-filled" width="16" height="16" className="modal-close-icon" />;

    const isLoadingState = isLoading === true;
    const isLoaded = !isLoadingState;

    // Explicit validation of all required data
    const hasAllRequiredData =
        tileData !== undefined &&
        tileData !== null &&
        stake_amount !== undefined &&
        win_amount !== undefined &&
        multiplier !== undefined;

    const shouldShowError = isLoaded && !hasAllRequiredData;

    const game = tileData ? toGameViewModel(tileData as GameType, { region, isMobile }) : null;
    const rgLogoUrl = typeof rgLogo === 'string' ? rgLogo : undefined;
    const rgLinkUrl = typeof rgLink === 'string' ? rgLink : undefined;

    return (
        <GameInfoModalWrapper
            show={type === POPUPS.BIGGEST_WIN}
            onClose={closePopup}
            backgroundImgUrl={backgroundImgUrl}
            isNarrow={true}
            closeIcon={closeIcon}
            variant="biggest-win"
            headerTitle={
                <span className="biggest-win-modal__header-title">
                    {getText({ text: 'components.biggestWin.title' })}
                </span>
            }>
            <div className="biggest-win-modal" data-testid="biggest-win-modal">
                {isLoadingState ? (
                    <div className="biggest-win-modal__loading">
                        <Spinner />
                    </div>
                ) : shouldShowError ? (
                    <div className={'biggest-win-modal__error-message'} data-testid="biggest-win-error-message">
                        <SvgLoader iconName="error-hexagon" className="biggest-win-modal__error-icon" />
                        {getText({ text: 'components.biggestWin.errorMessage' })}
                    </div>
                ) : !hasAllRequiredData || !game ? null : (
                    <>
                        <div className="biggest-win-modal__info">
                            <div className="biggest-win-modal__info-title">
                                {getText({ text: 'components.biggestWin.bigWin' })}
                            </div>
                            <div className="biggest-win-modal__info-amount">
                                <span className="biggest-win-modal__info-amount-value">
                                    <SpinningNumber value={win_amount} />
                                </span>
                            </div>
                            <div className="biggest-win-modal__info-details">
                                <span className="biggest-win-modal__info-details-text">
                                    {renderWinMessage(stake_amount, win_amount, multiplier)}
                                </span>
                            </div>
                        </div>
                        <div className="biggest-win-modal__tile-container">
                            <div className="biggest-win-modal__tile">
                                <GameBadgeImage
                                    logoUrl={game.logoUrl}
                                    backgroundUrl={game.backgroundUrl}
                                    customBackgroundColor={game.customBackgroundColor}
                                    name={game.name}
                                />
                            </div>
                        </div>
                        <div className="biggest-win-modal__title">{game.name}</div>
                        <div className="biggest-win-modal__cta">
                            <GameUrl
                                launcherUrlProps={game.launcherUrlProps}
                                gameName={game.name}
                                module="biggest_win_modal"
                                amplitudeEventProps={{
                                    event: 'Game Selected',
                                    Module: 'biggest_win_module',
                                    Category: 'biggest_win',
                                    'Zone Name': 'biggest_win'
                                }}>
                                <span className="button play-now-btn biggest-win-modal__cta-button">
                                    {getText({ text: 'common.playAgain' })}
                                </span>
                            </GameUrl>
                        </div>
                        {rgLogoUrl && (
                            <div className="biggest-win-modal__rg">
                                <img src={rgLogoUrl} className="biggest-win-modal__rg-logo" alt="regulatory logo" />
                                <span className="biggest-win-modal__rg-text">
                                    {getText({ text: 'components.biggestWin.rg.part1' })}
                                    {rgLinkUrl && (
                                        <a
                                            href={rgLinkUrl}
                                            className="biggest-win-modal__rg-link"
                                            rel="external"
                                            target="_blank">
                                            {getText({ text: 'components.biggestWin.rg.part2' })}
                                        </a>
                                    )}
                                    <span>.</span>
                                </span>
                            </div>
                        )}
                    </>
                )}
            </div>

            {lottieData && showLottie && (
                <div className="biggest-win-modal__lottie-animation">
                    <Lottie
                        options={{
                            animationData: lottieData,
                            loop: false,
                            autoplay: true
                        }}
                        width="150%" // at 100% for some reason the confetti are smaller compared to casino-web so we incresed the size
                        height="150%"
                    />
                </div>
            )}
        </GameInfoModalWrapper>
    );
};
